/**
 * The application's default themes, verbatim: Light Blue (`.theme-light` +
 * `.theme-light-blue`) and Dark Blue (`.dark` + `.theme-dark-blue`), as
 * declared in twin-app/src/app/globals.css. AppSurface writes one of these
 * sets onto its wrapper so a bridged product component paints exactly the
 * colours it paints in the application, whatever the marketing palette.
 *
 * Transcribed from the application's stylesheet; `app-theme-tokens.test.ts`
 * re-reads that stylesheet and names every token that differs, so a theme
 * change in the app fails here until this copy follows it.
 */

export type AppThemeTone = "light" | "dark";

export const APP_THEME_TOKENS: Record<AppThemeTone, Readonly<Record<string, string>>> = {
  light: {
    "--background": "0 0% 98%",
    "--foreground": "0 0% 8%",
    "--card": "0 0% 97%",
    "--card-foreground": "0 0% 8%",
    "--popover": "0 0% 100%",
    "--popover-foreground": "0 0% 8%",
    "--primary": "217 91% 35%",
    "--primary-foreground": "0 0% 100%",
    "--primary-dark": "217 91% 30%",
    "--link": "217 91% 35%",
    "--link-hover": "217 91% 30%",
    "--solid": "217 91% 38%",
    "--solid-hover": "217 91% 35%",
    "--solid-foreground": "0 0% 100%",
    "--secondary": "0 0% 97%",
    "--secondary-foreground": "0 0% 12%",
    "--muted": "0 0% 93%",
    "--muted-foreground": "0 0% 38%",
    "--tertiary-foreground": "0 0% 42%",
    "--placeholder": "0 0% 42%",
    "--accent": "0 0% 96%",
    "--accent-foreground": "0 0% 12%",
    "--destructive": "0 70% 38%",
    "--destructive-foreground": "0 0% 100%",
    "--border": "0 0% 91%",
    "--control-border": "0 0% 45%",
    "--control": "0 0% 93.5%",
    "--input": "0 0% 87%",
    "--ring": "var(--primary)",
    "--surface": "0 0% 99%",
    "--surface-raised": "0 0% 99.5%",
    "--content-code-bg": "0 0% 95%",
    "--content-code-border": "0 0% 88%",
    "--content-code-foreground": "0 0% 12%",
    "--code-keyword": "263 55% 42%",
    "--code-string": "150 60% 28%",
    "--code-number": "28 85% 34%",
    "--code-comment": "220 8% 48%",
    "--code-decorator": "200 85% 32%",
    "--badge-outline-bg": "0 0% 94%",
    "--badge-outline-border": "0 0% 82%",
    "--badge-outline-foreground": "0 0% 12%",
    "--badge-secondary-bg": "0 0% 96%",
    "--badge-secondary-foreground": "0 0% 12%",
    "--badge-primary-tone": "var(--primary)",
    "--badge-primary-foreground": "var(--primary-dark)",
    "--badge-green-tone": "145 65% 36%",
    "--badge-green-foreground": "145 70% 27%",
    "--badge-amber-tone": "38 88% 42%",
    "--badge-amber-foreground": "32 85% 29%",
    "--badge-red-tone": "0 75% 52%",
    "--badge-red-foreground": "0 65% 42%",
    "--badge-sky-tone": "199 82% 45%",
    "--badge-sky-foreground": "201 78% 34%",
    "--badge-violet-tone": "263 72% 57%",
    "--badge-violet-foreground": "263 68% 42%",
    "--badge-teal-tone": "173 72% 34%",
    "--badge-teal-foreground": "173 72% 25%",
    "--badge-orange-tone": "25 88% 48%",
    "--badge-orange-foreground": "22 82% 36%",
    "--badge-blue-tone": "228 72% 56%",
    "--badge-blue-foreground": "228 66% 42%",
    "--badge-plum-tone": "290 58% 50%",
    "--badge-plum-foreground": "290 55% 38%",
    "--badge-pink-tone": "325 70% 52%",
    "--badge-pink-foreground": "325 65% 40%",
    "--badge-rose-tone": "348 75% 55%",
    "--badge-rose-foreground": "348 68% 43%",
    "--badge-lime-tone": "82 60% 38%",
    "--badge-lime-foreground": "82 62% 26%",
    "--badge-stone-tone": "30 30% 44%",
    "--badge-stone-foreground": "30 32% 32%",
    "--badge-slate-tone": "215 26% 46%",
    "--badge-slate-foreground": "215 30% 34%",
    "--success": "142 70% 27%",
    "--success-foreground": "0 0% 100%",
    "--success-dark": "142 70% 22%",
    "--success-surface": "140 44% 93%",
    "--success-surface-border": "140 32% 78%",
    "--warning": "32 85% 29%",
    "--warning-foreground": "0 0% 100%",
    "--info": "201 78% 30%",
    "--info-foreground": "0 0% 100%",
    "--table-vline": "hsl(var(--border))",
    "--table-vline-subtle": "hsl(var(--border) / 0.6)",
    "--page-toolbar-bg": "hsl(var(--background))",
    "--kanban-lane-bg": "hsl(var(--muted) / 0.03)",
    "--kanban-card-bg": "hsl(var(--surface-raised))",
    "--kanban-card-hover-bg": "hsl(var(--muted))",
    "--chat-user-bg": "var(--control)",
    "--chat-user-border": "var(--border)",
    "--sidebar-panel": "0 0% 96%",
    "--sidebar-background": "0 0% 97%",
    "--sidebar-foreground": "var(--foreground)",
    "--sidebar-primary": "var(--primary)",
    "--sidebar-primary-foreground": "var(--primary-foreground)",
    "--sidebar-accent": "var(--accent)",
    "--sidebar-accent-foreground": "var(--accent-foreground)",
    "--sidebar-border": "var(--border)",
    "--sidebar-ring": "var(--ring)",
    "--sidebar-active": "0 0% 95%",
    "--chart-1": "hsl(217 91% 45%)",
    "--chart-2": "hsl(173 68% 28%)",
    "--chart-3": "hsl(17 88% 38%)",
    "--chart-4": "hsl(38 85% 31%)",
    "--chart-5": "hsl(0 65% 42%)",
    "--radius": "0.5rem",
  },
  dark: {
    "--background": "225 8% 8%",
    "--foreground": "0 0% 94%",
    "--card": "var(--sidebar-panel)",
    "--card-foreground": "0 0% 96%",
    "--popover": "225 7% 13%",
    "--popover-foreground": "0 0% 96%",
    "--primary": "216 88% 76%",
    "--primary-foreground": "225 8% 8%",
    "--primary-dark": "216 90% 82%",
    "--link": "216 88% 76%",
    "--link-hover": "216 90% 82%",
    "--solid": "218 72% 39%",
    "--solid-hover": "217 78% 40%",
    "--solid-foreground": "0 0% 100%",
    "--secondary": "225 8% 11%",
    "--secondary-foreground": "0 0% 94%",
    "--muted": "225 7% 12%",
    "--muted-foreground": "0 0% 76%",
    "--tertiary-foreground": "0 0% 67%",
    "--placeholder": "0 0% 63%",
    "--accent": "225 7% 13%",
    "--accent-foreground": "0 0% 94%",
    "--destructive": "4 82% 72%",
    "--destructive-foreground": "225 8% 8%",
    "--border": "225 6% 18%",
    "--control-border": "0 0% 48%",
    "--control": "225 7% 11%",
    "--input": "0 0% 11%",
    "--ring": "218 72% 60%",
    "--surface": "225 7% 12%",
    "--surface-raised": "225 7% 13%",
    "--content-code-bg": "0 0% 11%",
    "--content-code-border": "0 0% 20%",
    "--content-code-foreground": "0 0% 94%",
    "--code-keyword": "263 70% 78%",
    "--code-string": "145 45% 68%",
    "--code-number": "36 85% 70%",
    "--code-comment": "220 6% 58%",
    "--code-decorator": "199 75% 70%",
    "--badge-outline-bg": "var(--background)",
    "--badge-outline-border": "var(--border)",
    "--badge-outline-foreground": "var(--foreground)",
    "--badge-secondary-bg": "var(--secondary)",
    "--badge-secondary-foreground": "var(--secondary-foreground)",
    "--badge-primary-tone": "var(--primary)",
    "--badge-primary-foreground": "var(--primary-dark)",
    "--badge-green-tone": "145 45% 50%",
    "--badge-green-foreground": "145 42% 68%",
    "--badge-amber-tone": "40 55% 55%",
    "--badge-amber-foreground": "40 52% 70%",
    "--badge-red-tone": "0 55% 58%",
    "--badge-red-foreground": "0 52% 72%",
    "--badge-sky-tone": "199 55% 56%",
    "--badge-sky-foreground": "199 52% 71%",
    "--badge-violet-tone": "263 50% 62%",
    "--badge-violet-foreground": "263 48% 75%",
    "--badge-teal-tone": "173 45% 50%",
    "--badge-teal-foreground": "173 42% 68%",
    "--badge-orange-tone": "25 58% 56%",
    "--badge-orange-foreground": "25 55% 70%",
    "--badge-blue-tone": "228 55% 64%",
    "--badge-blue-foreground": "228 52% 76%",
    "--badge-plum-tone": "290 45% 60%",
    "--badge-plum-foreground": "290 44% 73%",
    "--badge-pink-tone": "325 52% 60%",
    "--badge-pink-foreground": "325 50% 73%",
    "--badge-rose-tone": "348 55% 62%",
    "--badge-rose-foreground": "348 52% 74%",
    "--badge-lime-tone": "82 42% 50%",
    "--badge-lime-foreground": "82 40% 66%",
    "--badge-stone-tone": "30 22% 58%",
    "--badge-stone-foreground": "30 20% 72%",
    "--badge-slate-tone": "215 22% 60%",
    "--badge-slate-foreground": "215 22% 74%",
    "--success": "142 60% 45%",
    "--success-foreground": "225 8% 8%",
    "--success-dark": "142 60% 64%",
    "--success-surface": "143 28% 15%",
    "--success-surface-border": "143 24% 28%",
    "--warning": "38 85% 60%",
    "--warning-foreground": "0 0% 10%",
    "--info": "197 90% 64%",
    "--info-foreground": "0 0% 10%",
    "--table-vline": "hsl(var(--border))",
    "--table-vline-subtle": "hsl(var(--border) / 0.6)",
    "--page-toolbar-bg": "hsl(var(--background))",
    "--kanban-lane-bg": "hsl(var(--muted) / 0.03)",
    "--kanban-card-bg": "hsl(var(--surface-raised))",
    "--kanban-card-hover-bg": "color-mix( in oklab, hsl(var(--foreground)) 4%, hsl(var(--surface-raised)) )",
    "--chat-user-border": "var(--border)",
    "--sidebar-panel": "225 8% 10%",
    "--sidebar-background": "225 8% 8%",
    "--sidebar-foreground": "0 0% 94%",
    "--sidebar-primary": "var(--primary)",
    "--sidebar-primary-foreground": "var(--primary-foreground)",
    "--sidebar-accent": "225 7% 13%",
    "--sidebar-accent-foreground": "0 0% 94%",
    "--sidebar-border": "var(--border)",
    "--sidebar-ring": "var(--ring)",
    "--sidebar-active": "0 0% 12%",
    "--chart-1": "hsl(217 78% 67%)",
    "--chart-2": "hsl(158 60% 55%)",
    "--chart-3": "hsl(27 78% 58%)",
    "--chart-4": "hsl(42 85% 65%)",
    "--chart-5": "hsl(4 66% 70%)",
    "--chat-user-bg": "var(--control)",
    "--radius": "0.5rem",
  },
};
